import {
  Action,
  ActionInstance,
  AppDataSchema,
  AppDataSchemaInstance,
  AppEvent,
  AppEventSubscription,
  AppEventType,
  Connection,
  Connector,
  Customer,
  DataLinkTable,
  DataLinkTableInstance,
  DataSource,
  DataSourceInstance,
  ExternalEventPull,
  ExternalEventSubscription,
  FieldMapping,
  FieldMappingInstance,
  Flow,
  FlowInstance,
  FlowRun,
  Integration,
  Scenario,
} from '@integration-app/sdk'

export const EXTERNAL_APPS_ROUTE = '/external-apps'
export const APPS_ROUTE = `${EXTERNAL_APPS_ROUTE}/integrations`

export const CONNECTORS_ROUTE = '/connectors'
export const PUBLIC_CONNECTORS_ROUTE = `${CONNECTORS_ROUTE}/public`
export const LOCAL_CONNECTORS_ROUTE = `${CONNECTORS_ROUTE}/local`

export const SCENARIOS_ROUTE = `/scenarios`
export const ACTIONS_ROUTE = `/actions`
export const DATA_SOURCES_ROUTE = `/data-sources`
export const DATA_LINK_TABLES_ROUTE = `/data-link-tables`
export const FIELD_MAPPINGS_ROUTE = `/field-mappings`
export const FLOWS_ROUTE = `/flows`

export const YOUR_APP_ROUTE = `/your-app`
export const USER_INTERFACE_ROUTE = `${YOUR_APP_ROUTE}/ui`

export const APP_AUTH_API_ROUTE = `/auth-api`
export const APP_DATA_SCHEMAS_ROUTE = `/data-schemas`
export const APP_EVENT_TYPES_ROUTE = `/event-types`
export const APP_SCREENS_ROUTE = `/screens`

export const CONNECTIONS_ROUTE = '/connections'

export const DEPLOYMENTS_ROUTE = '/deployments'
export const CUSTOMERS_ROUTE = `${DEPLOYMENTS_ROUTE}/customers`
export const ACTION_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/action-instances`
export const FLOW_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/flow-instances`
export const FIELD_MAPPING_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/field-mapping-instances`
export const DATA_SOURCE_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/data-source-instances`
export const DATA_LINK_TABLE_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/data-link-table-instances`
export const APP_EVENT_SUBSCRIPTIONS_ROUTE = `${DEPLOYMENTS_ROUTE}/app-event-subscriptions`
export const EXTERNAL_EVENT_SUBSCRIPTIONS_ROUTE = `${DEPLOYMENTS_ROUTE}/external-event-subscriptions`
export const APP_DATA_SCHEMA_INSTANCES_ROUTE = `${DEPLOYMENTS_ROUTE}/app-data-schema-instances`

export const ACTIVITY_LOG_ROUTE = '/activity-log'

export const DASHBOARD_ROUTE = '/dashboard'
export const INTEGRATION_FRAMEWORK_ROUTE = '/integration-framework'

export const FLOW_RUNS_ROUTE = `${ACTIVITY_LOG_ROUTE}/flow-runs`
export const EXTERNAL_EVENT_LOG_RECORDS_ROUTE = `${ACTIVITY_LOG_ROUTE}/external-events`
export const EXTERNAL_EVENT_PULLS_ROUTE = `${ACTIVITY_LOG_ROUTE}/external-event-pulls`
export const EXTERNAL_API_LOGS_ROUTE = `${ACTIVITY_LOG_ROUTE}/external-api-logs`
export const EXTERNAL_WEBHOOK_LOGS_ROUTE = `${ACTIVITY_LOG_ROUTE}/external-webhook-logs`
export const APP_EVENTS_ROUTE = `${ACTIVITY_LOG_ROUTE}/app-events`

export const SETTINGS_ROUTE = '/settings'
export const SETTINGS_GENERAL_ROUTE = `${SETTINGS_ROUTE}/general`
export const SETTINGS_TESTING_ROUTE = `${SETTINGS_ROUTE}/testing`
export const SETTINGS_LIMITS_ROUTE = `${SETTINGS_ROUTE}/limits`
export const SETTINGS_USAGE_ROUTE = `${SETTINGS_ROUTE}/usage`
export const WEBHOOK_NOTIFICATIONS_ROUTE = `${SETTINGS_ROUTE}/webhook-notifications`

export const MEMBRANE_ROUTE = '/membrane'
export const MEMBRANE_WORKSPACES_ROUTE = `${MEMBRANE_ROUTE}/workspaces`
export const MEMBRANE_RELEASE_MANAGEMENT_ROUTE = `${MEMBRANE_ROUTE}/release-management`

export const ORGANIZATION_ROUTE = '/manage-organization'
export const ORGANIZATION_SETTINGS_ROUTE = `${ORGANIZATION_ROUTE}/settings`
export const ORGANIZATION_ACCESS_ROUTE = `${ORGANIZATION_ROUTE}/access`
export const ORGANIZATION_USAGE_ROUTE = `${ORGANIZATION_ROUTE}/usage`
export const ORGANIZATION_BILLING_ROUTE = `${ORGANIZATION_ROUTE}/billing`

export const ACCOUNT_ROUTE = '/manage-account'
export const ACCOUNT_SETTINGS_ROUTE = `${ACCOUNT_ROUTE}/settings`
export const ACCOUNT_API_TOKEN_ROUTE = `${ACCOUNT_ROUTE}/api-token`

export const DOCS_ROUTE = '/docs'
export const DOCS_BASE_URL = 'https://console.integration.app/docs'

// Initial screen for new users
export const WELCOME_SCREEN_ROUTE = '/welcome'

export const routeTo = {
  integrations: () => APPS_ROUTE,
  integration: (integration: string | Integration) => {
    if (typeof integration === 'string') {
      return `${APPS_ROUTE}/${integration}`
    }
    return `${APPS_ROUTE}/${integration.id}`
  },

  apps: () => APPS_ROUTE,
  app: (id: string) => `${APPS_ROUTE}/${id}`,

  connectors: () => CONNECTORS_ROUTE,
  connector: (connector: Connector | string) => {
    if (typeof connector === 'string') {
      return `${CONNECTORS_ROUTE}/${connector}`
    }
    return `${CONNECTORS_ROUTE}/${connector.id}`
  },
  publicConnectors: () => PUBLIC_CONNECTORS_ROUTE,
  localConnectors: () => LOCAL_CONNECTORS_ROUTE,

  connections: () => CONNECTIONS_ROUTE,
  connection: (connection: Connection | string) => {
    if (typeof connection === 'string') {
      return `${CONNECTIONS_ROUTE}/${connection}`
    }
    if (connection.user?.id) {
      return withCustomerPrefix(connection, 'connections')
    }

    return `${CONNECTIONS_ROUTE}/${connection.id}`
  },

  flowRun: (flowRun: string | FlowRun) => {
    if (typeof flowRun === 'string') {
      return `${FLOW_RUNS_ROUTE}/${flowRun}`
    }
    return `${FLOW_RUNS_ROUTE}/${flowRun.id}`
  },

  flows: () => FLOWS_ROUTE,
  flow(
    flow: Flow | string,
    { toIntegrationSpecific } = { toIntegrationSpecific: true },
  ): string {
    if (typeof flow === 'string') {
      return `${FLOWS_ROUTE}/${flow}`
    }
    if (toIntegrationSpecific && flow.integrationId) {
      return withIntegrationPrefix(flow, 'flows')
    }
    if (flow.universalFlowId) {
      return `${FLOWS_ROUTE}/${flow.universalFlowId}/${flow.id}`
    }

    return `${FLOWS_ROUTE}/${flow.id}`
  },

  flowInstances: () => FLOW_INSTANCES_ROUTE,
  flowInstance(flowInstance: string | FlowInstance) {
    if (typeof flowInstance === 'string') {
      return `${FLOW_INSTANCES_ROUTE}/${flowInstance}`
    }
    if (flowInstance.user?.id) {
      return withCustomerPrefix(flowInstance, 'flows')
    }

    return `${FLOW_INSTANCES_ROUTE}/${flowInstance.id}`
  },

  fieldMappings: () => FIELD_MAPPINGS_ROUTE,
  fieldMapping: (
    fieldMapping: string | FieldMapping,
    { toIntegrationSpecific } = { toIntegrationSpecific: true },
  ) => {
    if (typeof fieldMapping === 'string') {
      return `${FIELD_MAPPINGS_ROUTE}/${fieldMapping}`
    }
    if (toIntegrationSpecific && fieldMapping.integrationId) {
      return withIntegrationPrefix(fieldMapping, 'field-mappings')
    }
    if (fieldMapping.universalFieldMappingId) {
      return `${FIELD_MAPPINGS_ROUTE}/${fieldMapping.universalFieldMappingId}/${fieldMapping.id}`
    }

    return `${FIELD_MAPPINGS_ROUTE}/${fieldMapping.id}`
  },

  fieldMappingInstances: () => FIELD_MAPPING_INSTANCES_ROUTE,
  fieldMappingInstance: (
    fieldMappingInstance: string | FieldMappingInstance,
  ) => {
    if (typeof fieldMappingInstance === 'string') {
      return `${FIELD_MAPPING_INSTANCES_ROUTE}/${fieldMappingInstance}`
    }
    if (fieldMappingInstance.user?.id) {
      return withCustomerPrefix(fieldMappingInstance, 'field-mappings')
    }

    return `${FIELD_MAPPING_INSTANCES_ROUTE}/${fieldMappingInstance.id}`
  },

  dataSources: () => DATA_SOURCES_ROUTE,
  dataSource: (
    dataSource: string | DataSource,
    { toIntegrationSpecific } = { toIntegrationSpecific: true },
  ) => {
    if (typeof dataSource === 'string') {
      return `${DATA_SOURCES_ROUTE}/${dataSource}`
    }
    if (toIntegrationSpecific && dataSource.integrationId) {
      return withIntegrationPrefix(dataSource, 'data-sources')
    }
    if (dataSource.universalDataSourceId) {
      return `${DATA_SOURCES_ROUTE}/${dataSource.universalDataSourceId}/${dataSource.id}`
    }

    return `${DATA_SOURCES_ROUTE}/${dataSource.id}`
  },

  dataSourceInstances: () => DATA_SOURCE_INSTANCES_ROUTE,
  dataSourceInstance: (dataSourceInstance: string | DataSourceInstance) => {
    if (typeof dataSourceInstance === 'string') {
      return `${DATA_SOURCE_INSTANCES_ROUTE}/${dataSourceInstance}`
    }
    if (dataSourceInstance.user?.id) {
      return withCustomerPrefix(dataSourceInstance, 'data-sources')
    }

    return `${DATA_SOURCE_INSTANCES_ROUTE}/${dataSourceInstance.id}`
  },

  externalEventLogRecord: (id: string) =>
    `${EXTERNAL_EVENT_LOG_RECORDS_ROUTE}/${id}`,
  externalEventPull: (externalEventPull: ExternalEventPull | string) => {
    if (typeof externalEventPull === 'string') {
      return `${EXTERNAL_EVENT_PULLS_ROUTE}/${externalEventPull}`
    }
    return `${EXTERNAL_EVENT_PULLS_ROUTE}/${externalEventPull.id}`
  },

  actions: () => ACTIONS_ROUTE,
  action: (
    action: Action | string,
    { toIntegrationSpecific } = { toIntegrationSpecific: true },
  ) => {
    if (typeof action === 'string') {
      return `${ACTIONS_ROUTE}/${action}`
    }
    if (toIntegrationSpecific && action.integrationId) {
      return withIntegrationPrefix(action, 'actions')
    }
    if (action.parentId) {
      return `${ACTIONS_ROUTE}/${action.parentId}/${action.id}`
    }

    return `${ACTIONS_ROUTE}/${action.id}`
  },

  actionInstances: () => ACTION_INSTANCES_ROUTE,
  actionInstance: (actionInstance: ActionInstance | string) => {
    if (typeof actionInstance === 'string') {
      return `${ACTION_INSTANCES_ROUTE}/${actionInstance}`
    }
    if (actionInstance.user?.id) {
      return withCustomerPrefix(actionInstance, 'actions')
    }

    return `${ACTION_INSTANCES_ROUTE}/${actionInstance.id}`
  },

  dataLinkTables: () => DATA_LINK_TABLES_ROUTE,
  dataLinkTable: (dataLinkTable: DataLinkTable | string) => {
    if (typeof dataLinkTable === 'string') {
      return `${DATA_LINK_TABLES_ROUTE}/${dataLinkTable}`
    }
    return `${DATA_LINK_TABLES_ROUTE}/${dataLinkTable.id}`
  },

  dataLinkTableInstances: () => DATA_LINK_TABLE_INSTANCES_ROUTE,
  dataLinkTableInstance: (
    dataLinkTableInstance: DataLinkTableInstance | string,
  ) => {
    if (typeof dataLinkTableInstance === 'string') {
      return `${DATA_LINK_TABLE_INSTANCES_ROUTE}/${dataLinkTableInstance}`
    }
    if (dataLinkTableInstance.user?.id) {
      return withCustomerPrefix(dataLinkTableInstance, 'data-link-tables')
    }

    return `${DATA_LINK_TABLE_INSTANCES_ROUTE}/${dataLinkTableInstance.id}`
  },

  appEvent: (appEvent: string | AppEvent) => {
    if (typeof appEvent === 'string') {
      return `${APP_EVENTS_ROUTE}/${appEvent}`
    }
    return `${APP_EVENTS_ROUTE}/${appEvent.id}`
  },
  appEventType: (appEventType: AppEventType | string) => {
    if (typeof appEventType === 'string') {
      return `${APP_EVENT_TYPES_ROUTE}/${appEventType}`
    }
    return `${APP_EVENT_TYPES_ROUTE}/${appEventType.id}`
  },

  appEventSubscriptions: () => APP_EVENT_SUBSCRIPTIONS_ROUTE,
  appEventSubscription: (
    appEventSubscription: AppEventSubscription | string,
  ) => {
    if (typeof appEventSubscription === 'string') {
      return `${APP_EVENT_SUBSCRIPTIONS_ROUTE}/${appEventSubscription}`
    }
    if (appEventSubscription.user?.id) {
      return withCustomerPrefix(appEventSubscription, 'app-event-subscriptions')
    }

    return `${APP_EVENT_SUBSCRIPTIONS_ROUTE}/${appEventSubscription.id}`
  },

  externalEventSubscriptions: () => EXTERNAL_EVENT_SUBSCRIPTIONS_ROUTE,
  externalEventSubscription: (
    externalEventSubscription: ExternalEventSubscription | string,
  ) => {
    if (typeof externalEventSubscription === 'string') {
      return `${EXTERNAL_EVENT_SUBSCRIPTIONS_ROUTE}/${externalEventSubscription}`
    }
    if (externalEventSubscription.user?.id) {
      return withCustomerPrefix(
        externalEventSubscription,
        'external-event-subscriptions',
      )
    }

    return `${EXTERNAL_EVENT_SUBSCRIPTIONS_ROUTE}/${externalEventSubscription.id}`
  },

  appAuthApi: () => APP_AUTH_API_ROUTE,
  appEventTypes: () => APP_EVENT_TYPES_ROUTE,

  appDataSchemas: () => APP_DATA_SCHEMAS_ROUTE,
  appDataSchema: (appDataSchema: AppDataSchema | string) => {
    if (typeof appDataSchema === 'string') {
      return `${APP_DATA_SCHEMAS_ROUTE}/${appDataSchema}`
    }
    return `${APP_DATA_SCHEMAS_ROUTE}/${appDataSchema?.id}`
  },

  appDataSchemaInstances: () => APP_DATA_SCHEMA_INSTANCES_ROUTE,
  appDataSchemaInstance: (
    appDataSchemaInstance: string | AppDataSchemaInstance,
  ) => {
    if (typeof appDataSchemaInstance === 'string') {
      return `${APP_DATA_SCHEMA_INSTANCES_ROUTE}/${appDataSchemaInstance}`
    }
    return `${APP_DATA_SCHEMA_INSTANCES_ROUTE}/${appDataSchemaInstance.id}`
  },

  scenario: (scenario: Scenario | string) => {
    if (typeof scenario === 'string') {
      return `${SCENARIOS_ROUTE}/${scenario}`
    }
    return `${SCENARIOS_ROUTE}/${scenario?.id}`
  },
  scenarios: () => SCENARIOS_ROUTE,

  customers: () => CUSTOMERS_ROUTE,
  customer: (customer: Customer | string) => {
    if (typeof customer === 'string') {
      return `${CUSTOMERS_ROUTE}/${customer}`
    }

    return `${CUSTOMERS_ROUTE}/${customer.id}`
  },

  externalApiLogs: () => EXTERNAL_API_LOGS_ROUTE,
  externalApiLog: (id: string) => `${EXTERNAL_API_LOGS_ROUTE}/${id}`,
  externalWebhookLog: (id: string) => `${EXTERNAL_WEBHOOK_LOGS_ROUTE}/${id}`,

  screens: () => APP_SCREENS_ROUTE,
}

function withCustomerPrefix(instance: any, elemRoute: string) {
  const prefix = routeTo.customer(instance.user as any)
  return `${prefix}/${elemRoute}/${instance.id}`
}

function withIntegrationPrefix(element: any, elemRoute: string) {
  const prefix = routeTo.integration(element.integrationId)
  return `${prefix}/${elemRoute}/${element.id}`
}
